export interface SalesOrderItem {
  id?: string;
  sales_order_id?: string;
  variant_id: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  discount_amount?: number;
  taxable_amount?: number;
  tax_rate?: number;
  cgst_amount?: number;
  sgst_amount?: number;
  igst_amount?: number;
  tax_amount?: number;
  line_total?: number;
  description?: string;
  hsn_code?: string;
  created_at?: string;
  updated_at?: string;
  variant?: {
    id: string;
    name: string;
    sku: string;
    mrp: number;
    sale_price: number;
    hsn_code?: string;
    product?: {
      id: string;
      name: string;
      item_code: string;
    };
  };
}

export interface OtherCharge {
  name: string;
  amount: number;
  description?: string;
}

export enum SalesOrderStatus {
  DRAFT = 'draft',
  CONFIRMED = 'confirmed',
  PROCESSING = 'processing',
  READY_TO_SHIP = 'ready_to_ship',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  INVOICED = 'invoiced',
  CANCELLED = 'cancelled'
}

export interface SalesOrder {
  id: string;
  order_number: string;
  customer_id: string;
  order_date: string;
  expected_delivery_date?: string;
  billing_address_id?: string;
  shipping_address_id?: string;
  shipping_charges: number;
  packing_charges: number;
  other_charges: OtherCharge[];
  notes?: string;
  status: SalesOrderStatus;
  warehouse_id?: string;

  // Source tracking
  estimate_id?: string;
  
  // Financial details
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total_amount: number;
  payment_status?: string;
  razorpay_order_id?: string;
  razorpay_payment_id?: string;
  
  // Conversion tracking
  converted_to_invoice_id?: string;
  invoiced_at?: string;
  
  // Shipment tracking
  awb_number?: string;
  tracking_url?: string;
  shipment_status?: string;
  carrier_name?: string;
  
  // Admin attribution
  created_by_admin_id: string;
  updated_by_admin_id?: string;
  status_changed_at?: string;
  status_changed_by_admin_id?: string;
  
  // Timestamps
  created_at: string;
  updated_at?: string;
  
  // Relationships
  items: SalesOrderItem[];
  customer?: {
    id: string;
    name: string;
    billing_name: string;
    contact_person: string;
    email?: string;
    mobile_no?: string;
    phone?: string;
  };
  created_by_admin?: {
    id: string;
    name: string;
    email: string;
  };
  estimate?: {
    id: string;
    estimate_number: string;
    status?: string;
  };
  invoice?: {
    id: string;
    invoice_number: string;
    status?: string;
  };
  billing_address?: {
    id: string;
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country?: string;
    contact_person?: string;
    contact_phone?: string;
  };
  shipping_address?: {
    id: string;
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country?: string;
    contact_person?: string;
    contact_phone?: string;
  };
  warehouse?: {
    id: string;
    name: string;
  };
}

export interface SalesOrderCreate {
  customer_id: string;
  order_date: string;
  expected_delivery_date?: string;
  billing_address_id?: string;
  shipping_address_id?: string;
  shipping_charges?: number;
  packing_charges?: number;
  other_charges?: OtherCharge[];
  notes?: string;
  warehouse_id?: string;
  items: Omit<SalesOrderItem, 'id' | 'sales_order_id' | 'created_at' | 'updated_at' | 'variant'>[];
}

export interface SalesOrderUpdate {
  customer_id?: string;
  order_date?: string;
  expected_delivery_date?: string;
  billing_address_id?: string;
  shipping_address_id?: string;
  shipping_charges?: number;
  packing_charges?: number;
  other_charges?: OtherCharge[];
  notes?: string;
  warehouse_id?: string;
  items?: Omit<SalesOrderItem, 'id' | 'sales_order_id' | 'created_at' | 'updated_at' | 'variant'>[];
}

export interface SalesOrderStatusUpdate {
  status: SalesOrderStatus;
  notes?: string;
}

export interface SalesOrderConversionRequest {
  estimate_id: string;
  order_date?: string;
  expected_delivery_date?: string;
  shipping_charges?: number;
  packing_charges?: number;
  other_charges?: OtherCharge[];
  notes?: string;
  warehouse_id?: string;
}

export interface SalesOrderSearchFilters {
  customer_id?: string;
  status?: SalesOrderStatus;
  order_date_from?: string;
  order_date_to?: string;
  expected_delivery_date_from?: string;
  expected_delivery_date_to?: string;
  search_term?: string;
  created_by_admin_id?: string;
  estimate_id?: string;
}

export interface SalesOrderListResponse {
  sales_orders: SalesOrder[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Status color mapping for consistent UI
export const SALES_ORDER_STATUS_COLORS: Record<SalesOrderStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [SalesOrderStatus.DRAFT]: 'default',
  [SalesOrderStatus.CONFIRMED]: 'info',
  [SalesOrderStatus.PROCESSING]: 'warning',
  [SalesOrderStatus.READY_TO_SHIP]: 'primary',
  [SalesOrderStatus.SHIPPED]: 'primary',
  [SalesOrderStatus.DELIVERED]: 'success',
  [SalesOrderStatus.INVOICED]: 'success',
  [SalesOrderStatus.CANCELLED]: 'error'
};

// Status labels for display
export const SALES_ORDER_STATUS_LABELS: Record<SalesOrderStatus, string> = {
  [SalesOrderStatus.DRAFT]: 'Draft',
  [SalesOrderStatus.CONFIRMED]: 'Confirmed',
  [SalesOrderStatus.PROCESSING]: 'Processing',
  [SalesOrderStatus.READY_TO_SHIP]: 'Ready to Ship',
  [SalesOrderStatus.SHIPPED]: 'Shipped',
  [SalesOrderStatus.DELIVERED]: 'Delivered',
  [SalesOrderStatus.INVOICED]: 'Invoiced',
  [SalesOrderStatus.CANCELLED]: 'Cancelled'
};

export interface SalesOrderConversionResponse {
  success: boolean;
  sales_order_id: string;
  sales_order_number: string;
  estimate_id: string;
  estimate_number: string;
  message: string;
}

export interface SalesOrderToInvoiceConversionResponse {
  success: boolean;
  invoice_id: string;
  invoice_number: string;
  sales_order_id: string;
  sales_order_number: string;
  total_amount: number;
  due_date: string;
  message: string;
}